import { axiosInstance } from "./AdminServices";
import { sendEmail } from "./EmailSender";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import emailjs from "@emailjs/browser";

// Fetch prayer requests with optional filters
const fetchPrayerRequests = async (params) => {
  try {
    console.log("fetchPrayerRequests params", params);
    const response = await axiosInstance.get(`/prayer-request`, { params });
    console.log("fetchPrayerRequests response", response?.data?.length);
    return response.data;
  } catch (err) {
    console.error("Error fetching prayer requests", err);
    throw new Error(
      err.response?.data.message || " Error fetching prayer requests"
    );
  }
};

const updatePrayerReqStatus = async (prayer, newStatus, userID) => {
  try {
    console.log("updatePrayerReqStatus", prayer, userID);
    const response = await axiosInstance.put(
      `/prayer-request/${prayer.id}/status`,
      {
        prayer: prayer,
        actionedBy: userID,
        status: newStatus,
      }
    );
    console.log("updatePrayerReqStatus response", response);
    if (newStatus === "Actioned") {
      if (
        response?.data?.email !== process.env.REACT_APP_EMAILJS_PRAYER_EMAIL_ID
      ) {
        try {
          await sendEmail(response.data, "prayerReply", "service_prayer");
        } catch (err) {
          console.error("Error sending email", err);
        }
      }
    } else if (newStatus === "Blessed") {
      //TBD: send email to user
      if (
        response.data.messageFromMarian &&
        response.data.messageFromMarian.trim().length > 0
      ) {
        try {
          const emailResponse = await sendEmail(
            response.data,
            "prayerReplyMarian",
            "service_prayer"
          );
        } catch (err) {
          console.error("Error sending email", err);
        }
      }
    }
    return response.data;
  } catch (err) {
    console.error("Error updating prayer request status", err);
    throw new Error(
      err.response?.data.message || " Error updating prayer request status"
    );
  }
};
const updatePrayerReq = async (prayer, userID) => {
  try {
    console.log("updatePrayerReqStatus", prayer, userID);
    const response = await axiosInstance.put(
      `/prayer-request/${prayer.id}/status`,
      {
        prayer: prayer,
        actionedBy: userID,
        status: prayer.status,
      }
    );
    console.log("updatePrayerReqStatus response", response);
    return response.data;
  } catch (err) {
    console.error("Error updating prayer request status", err);
    throw new Error(
      err.response?.data.message || " Error updating prayer request status"
    );
  }
};
async function savePrayerRequest(formData) {
  const response = await axiosInstance.post(`/prayer-request`, formData);
  if (response.status === 200) {
    const emailResponse = await sendEmail(formData, "prayer", "service_prayer");
    if (!emailResponse.success) {
      throw new Error("Error in sending email");
    }
  } else {
    throw new Error("Error saving prayer request");
  }
  return response;
} // Export the functions

function buildStyledWorkbook(data) {
  if (!data || data.length === 0) {
    throw new Error("No data provided to build Excel workbook.");
  }

  // Convert JSON to worksheet
  const ws = XLSX.utils.json_to_sheet(data);

  // Define column widths dynamically or use default
  const columnCount = Object.keys(data[0]).length;
  ws["!cols"] = new Array(columnCount).fill(null).map((_, index) => {
    return { wch: index === 0 ? 30 : index === 1 ? 70 : 20 }; // custom width logic
  });

  // Header style
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "4F81BD" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top: { style: "thin", color: { auto: 1 } },
      bottom: { style: "thin", color: { auto: 1 } },
      left: { style: "thin", color: { auto: 1 } },
      right: { style: "thin", color: { auto: 1 } },
    },
  };

  // Apply styles to header row
  const headers = Object.keys(data[0]);
  headers.forEach((_, colIndex) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
    if (ws[cellRef]) {
      ws[cellRef].s = headerStyle;
    }
  });

  // Optional: Improve printing and layout
  ws["!pageSetup"] = {
    orientation: "landscape",
    fitToPage: true,
    fitToHeight: 1,
    fitToWidth: 1,
  };

  // Build workbook and append worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "PrayerRequests");

  return wb;
}

const sendPrayerRequestsReport = async (prayerRequests) => {
  try {
    console.log(
      `[${new Date().toISOString()}] Preparing weekly prayer requests report...`
    );

    const hasRequests = prayerRequests.length > 0;
    const subject = "🕊️ Weekly Prayer Requests🕊️";
    const to_email = process.env.REACT_APP_EMAILJS_PRAYER_EMAIL_ID;

    let attachmentBase64 = null;
    let fileName = "";

    if (hasRequests) {
      const data = prayerRequests.map((prayer) => ({
        SUMMARY: prayer.summary,
        "PRAYER DETAILS": prayer.messageInEnglish,
        Status: prayer.status,
        DATE: new Date(prayer.createdAt).toLocaleString(),
      }));

      const wb = buildStyledWorkbook(data);
      fileName = `prayer_requests_report_${new Date()
        .toLocaleString("en-GB", { timeZone: "UTC" })
        .replace(/[/]/g, "-")
        .replace(/[, ]/g, "_")
        .replace(/:/g, "")}.xlsx`;

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Convert Blob to base64
      attachmentBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]); // remove data:...;base64,
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }

    const formData = {
      to_name: "Marian Team",
      subject: subject,
      to_email: to_email,
      from_name: "Grace Anointing Intercessory Team",
      from_email: process.env.REACT_APP_EMAILJS_PRAYER_EMAIL_ID,
      phone: "N/A",
      message: hasRequests
        ? "Please find attached the weekly prayer requests to be prayed over."
        : "There are no new prayer requests to report this week.",
      attachments: attachmentBase64,
      fileName: fileName,
    };

    const result = await sendEmail(formData, "TMP_Default", "service_prayer");

    if (result.success) {
      const prayerRequestIds = prayerRequests.map((prayer) => prayer.id);
      const updateResponse = await axiosInstance.put(
        `/prayer-request/update-email-time`,
        {
          prayerRequestIds: prayerRequestIds,
          emailToMarianTime: new Date().toISOString(),
        }
      );
      console.log("updateResponse", updateResponse);
    } else {
      throw new Error("Failed to send weekly prayer request report email.");
    }

    console.log(`[${new Date().toISOString()}] ✅ Email sent successfully.`);
  } catch (error) {
    console.error(
      `[${new Date().toISOString()}] ❌ Error in sending report:`,
      error
    );
  }
};

/**
 * Sends an email with an XLSX file attachment using EmailJS.
 *
 * @param {Array<Object>} data - Data to convert to Excel.
 * @param {string} toEmail - Recipient email.
 * @param {string} subject - Subject of the email.
 * @param {string} message - Body message.
 * @param {string} templateId - EmailJS template ID.
 * @param {string} serviceId - EmailJS service ID.
 * @param {string} publicKey - EmailJS public key.
 * @returns {Promise<Object>} - Result of the email sending process.
 */
const sendEmailWithAttachment = async (prayerRequests) => {
  try {
    const subject = "🕊️ Weekly Prayer Requests";
    const to_email = process.env.REACT_APP_EMAILJS_PRAYER_EMAIL_ID;
    const hasRequests = prayerRequests.length > 0;
    const message = hasRequests
      ? "Please find attached the weekly prayer requests to be prayed over."
      : "There are no new prayer requests to report this week.";
    // 1. Create XLSX Workbook and Sheet
    const data = prayerRequests.map((prayer) => ({
      Summary: prayer.summary,
      "Prayer Request": prayer.messageInEnglish,
      "Created At": new Date(prayer.createdAt).toLocaleString(),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // 2. Generate XLSX file as base64
    const xlsBase64 = XLSX.write(wb, {
      bookType: "xlsx",
      type: "base64",
    });

    // 3. Set template params
    const templateParams = {
      to_email: to_email,
      subject: subject,
      message: message,
      attachments: xlsBase64,
    };

    // 4. Send email via EmailJS
    console.log("Sending email with attachment...");
    console.log("templateParams", templateParams);
    const response = await emailjs.send(
      "service_prayer",
      "TMP_Default",
      templateParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );

    console.log(
      "✅ Email sent with attachment:",
      response.status,
      response.text
    );
    return { success: true, status: response.status, text: response.text };
  } catch (error) {
    console.error("❌ Failed to send email with attachment:", error);
    return { success: false, error };
  }
};

export {
  fetchPrayerRequests,
  updatePrayerReqStatus,
  savePrayerRequest,
  updatePrayerReq,
  sendPrayerRequestsReport,
  buildStyledWorkbook,
};
